/**
 * @description
 * Interactive time-series chart component for visualizing domain activity over time.
 * Supports multiple chart types (line, area, bar) with zoom, pan, and interactive features.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/TimeSeriesChart.test.tsx
 */

import { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from 'recharts';
import { Download, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import type { TimeSeriesAggregation } from '../../../types/vscode-lens';
import { formatTimeLabel } from '../../utils/timeSeriesUtils';
import { getDomainColor } from '../../utils/domainUtils';

interface TimeSeriesChartProps {
  /** Time-series aggregation data */
  data: TimeSeriesAggregation;
  
  /** Chart type */
  chartType?: 'line' | 'area' | 'bar';
  
  /** Selected domains to display (empty = show all) */
  selectedDomains?: string[];
  
  /** Chart height in pixels */
  height?: number;
  
  /** Show legend */
  showLegend?: boolean;
  
  /** Show grid */
  showGrid?: boolean;
  
  /** Callback when time range is selected */
  onTimeRangeSelect?: (start: number, end: number) => void;
  
  /** Callback when domain is clicked */
  onDomainClick?: (domain: string) => void;
}

/**
 * Time-series chart component with interactive features
 */
export function TimeSeriesChart({
  data,
  chartType = 'line',
  selectedDomains = [],
  height = 400,
  showLegend = true,
  showGrid = true,
  onTimeRangeSelect,
  onDomainClick,
}: TimeSeriesChartProps) {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);

  // Prepare chart data
  const chartData = useMemo(() => {
    // Group data points by timestamp
    const timeMap = new Map<number, Record<string, number>>();
    
    data.dataPoints.forEach(point => {
      // Filter by selected domains if specified
      if (selectedDomains.length > 0 && !selectedDomains.includes(point.domain)) {
        return;
      }
      
      const existing = timeMap.get(point.timestamp) || { timestamp: point.timestamp };
      existing[point.domain] = point.count;
      timeMap.set(point.timestamp, existing);
    });
    
    // Convert to array and sort by timestamp
    return Array.from(timeMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [data.dataPoints, selectedDomains]);

  // Get domains to display
  const displayDomains = useMemo(() => {
    if (selectedDomains.length > 0) {
      return selectedDomains;
    }
    return data.topDomains.slice(0, 10); // Show top 10 by default
  }, [data.topDomains, selectedDomains]);

  // Handle zoom
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.5, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setBrushRange(null);
  };

  // Handle brush change
  const handleBrushChange = (range: { startIndex?: number; endIndex?: number }) => {
    if (range.startIndex !== undefined && range.endIndex !== undefined) {
      const start = chartData[range.startIndex]?.timestamp;
      const end = chartData[range.endIndex]?.timestamp;
      if (start && end) {
        setBrushRange([start, end]);
        onTimeRangeSelect?.(start, end);
      }
    }
  };

  // Export chart as image
  const handleExport = () => {
    // This would use html2canvas or similar library
    console.log('Export chart functionality to be implemented');
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color: string }>;
    label?: number;
  }) => {
    if (!active || !payload || payload.length === 0) return null;

    const timestamp = label as number;
    const formattedTime = formatTimeLabel(timestamp, data.granularity);

    return (
      <div className="rounded-lg border bg-card p-3 shadow-lg">
        <p className="font-medium mb-2">{formattedTime}</p>
        <div className="space-y-1">
          {payload.map((entry, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground">{entry.name}:</span>
              </div>
              <span className="font-medium">{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
          Total: {payload.reduce((sum: number, entry) => sum + entry.value, 0).toLocaleString()}
        </div>
      </div>
    );
  };

  // Render chart based on type
  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    const xAxisProps = {
      dataKey: 'timestamp',
      tickFormatter: (value: number) => formatTimeLabel(value, data.granularity),
      className: 'text-xs',
    };

    const yAxisProps = {
      className: 'text-xs',
    };

    const chartElements = displayDomains.map((domain, index) => {
      const color = getDomainColor(domain, index);
      const key = `domain-${domain}`;

      if (chartType === 'line') {
        return (
          <Line
            key={key}
            type="monotone"
            dataKey={domain}
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, onClick: () => onDomainClick?.(domain) }}
            name={domain}
          />
        );
      } else if (chartType === 'area') {
        return (
          <Area
            key={key}
            type="monotone"
            dataKey={domain}
            stackId="1"
            stroke={color}
            fill={color}
            fillOpacity={0.6}
            name={domain}
          />
        );
      } else {
        return (
          <Bar
            key={key}
            dataKey={domain}
            fill={color}
            name={domain}
            onClick={() => onDomainClick?.(domain)}
            className="cursor-pointer"
          />
        );
      }
    });

    const ChartComponent = chartType === 'line' ? LineChart : chartType === 'area' ? AreaChart : BarChart;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent {...commonProps}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />}
          <XAxis {...xAxisProps} />
          <YAxis {...yAxisProps} />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              onClick={(e) => onDomainClick?.(e.value)}
              className="cursor-pointer"
            />
          )}
          {chartElements}
          {chartData.length > 20 && (
            <Brush
              dataKey="timestamp"
              height={30}
              stroke="#8884d8"
              tickFormatter={(value: number) => formatTimeLabel(value, data.granularity)}
              onChange={handleBrushChange}
            />
          )}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <p>No time-series data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 5}
            className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            onClick={handleResetZoom}
            disabled={zoomLevel === 1 && !brushRange}
            className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reset View"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {chartData.length} data points
          </span>
          <button
            onClick={handleExport}
            className="p-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Export Chart"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-lg border bg-card p-4">
        {renderChart()}
      </div>

      {/* Info */}
      {brushRange && (
        <div className="text-sm text-muted-foreground">
          Selected range: {formatTimeLabel(brushRange[0], data.granularity)} to{' '}
          {formatTimeLabel(brushRange[1], data.granularity)}
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */